import { useMemo, useRef } from "react";

import { authHeader, formsFetch, unwrap } from "../lib/formsApi";
import type { Answer } from "../questionnaire/answers";
import { downloadFile, type FileActions, openAttachment } from "../questionnaire/fileActions";

export const useAnswerFilesV2 = (
    assessmentId: string,
    tenant: string | undefined,
    token: string | undefined,
    answerId: (key: string, answer?: Answer) => Promise<string>
): FileActions => {
    const latest = useRef({ assessmentId, tenant, token, answerId });
    latest.current = { assessmentId, tenant, token, answerId };

    return useMemo(
        () => ({
            upload: async (key, answer, file) => {
                const { assessmentId, token, answerId } = latest.current;
                const referenceId = await answerId(key, answer ?? {});
                const { url, uniqueFileName, fileId, requestHeaders } = unwrap(
                    await formsFetch.POST("/api/files/upload-url", {
                        headers: authHeader(token),
                        body: {
                            referenceId,
                            fileName: file.name,
                            metaJson: JSON.stringify({ origin: "assessment", questionId: key, assessmentId }),
                        },
                    }),
                    "create upload url"
                );
                const headers = new Headers();
                requestHeaders?.forEach((header) => headers.set(header.key, header.value));
                const response = await fetch(url, { method: "PUT", headers, body: file });
                if (!response.ok) throw new Error(`Upload request failed with status ${response.status}`);
                return { fileName: uniqueFileName, fileId };
            },
            download: async (key, fileName) => {
                const { token, answerId } = latest.current;
                await downloadFile(token, await answerId(key), fileName);
            },
            openAttachment: (path) => openAttachment(latest.current.token, path),
            remove: async (key, fileName) => {
                const { token, answerId } = latest.current;
                const referenceId = await answerId(key);
                const result = await formsFetch.DELETE("/api/files/{referenceId}/{fileName}", {
                    headers: authHeader(token),
                    params: { path: { referenceId, fileName } },
                });
                if (result.response.status !== 404) unwrap(result, "delete file");
            },
            suggest: async (fileId) => {
                const { token } = latest.current;
                return unwrap(
                    await formsFetch.GET("/api/files/{fileId}/suggestions", {
                        headers: authHeader(token),
                        params: { path: { fileId } },
                    }),
                    "get file suggestions"
                );
            },
            suggestAnswers: async (key, files) => {
                const { assessmentId, tenant, token, answerId } = latest.current;
                const referenceId = await answerId(key);
                const { suggestions } = unwrap(
                    await formsFetch.POST("/api/suggest-answers", {
                        headers: authHeader(token),
                        body: { assessmentId, paths: files.map((f) => `${tenant}/${referenceId}/${f.fileName}`) },
                    }),
                    "suggest answers"
                );
                return suggestions;
            },
        }),
        []
    );
};
